/**
 * The HTTP surface, as data.
 *
 * Route strings live here so the client never hand-writes a URL and the server
 * never invents one. `ApiRoutes` builds paths; `ApiResponseMap` documents what
 * each endpoint returns so the fetch wrapper can be typed end to end.
 */
/** Mounted prefix for every JSON endpoint. Anything else is the SPA. */
export const API_PREFIX = '/api';
export const ApiRoutes = {
    health: () => `${API_PREFIX}/health`,
    // ── Reference data (public, read-only) ──
    funds: () => `${API_PREFIX}/funds`,
    businessModels: () => `${API_PREFIX}/business-models`,
    fund: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}`,
    fundSummary: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/summary`,
    // ── Series (public, read-only) ──
    navMonthly: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/nav/monthly`,
    navDaily: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/nav/daily`,
    holdings: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/holdings`,
    holdingPeriods: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/holdings/periods`,
    // ── Exports (public, read-only) ──
    exportNavDailyCsv: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/nav/daily.csv`,
    exportNavMonthlyCsv: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/nav/monthly.csv`,
    // ── Live market data (public, read-only, cached server-side) ──
    marketSnapshot: () => `${API_PREFIX}/market/snapshot`,
    // ── AI insights ──
    // The read is public and answers from the database; it never triggers an LLM
    // call. Everything that can spend money is admin-only, which is what keeps
    // decision D3 ("the link is the credential") from also meaning "the link is a
    // bearer token for our API budget".
    insights: (code) => `${API_PREFIX}/funds/${encodeURIComponent(code)}/insights`,
    insightsGenerate: () => `${API_PREFIX}/insights/generate`,
    insightDrafts: () => `${API_PREFIX}/insights/drafts`,
    insightReview: (id) => `${API_PREFIX}/insights/${id}/review`,
    insightRuns: () => `${API_PREFIX}/insights/runs`,
    insightRunCards: (id) => `${API_PREFIX}/insights/runs/${id}/cards`,
    // ── Auth ──
    login: () => `${API_PREFIX}/auth/login`,
    logout: () => `${API_PREFIX}/auth/logout`,
    me: () => `${API_PREFIX}/auth/me`,
    // ── Templates (public download) ──
    template: (dataset, format = 'xlsx') => `${API_PREFIX}/templates/${dataset}.${format}`,
    // ── Uploads (admin only) ──
    uploadValidate: () => `${API_PREFIX}/uploads/validate`,
    uploadCommit: () => `${API_PREFIX}/uploads/commit`,
    uploads: () => `${API_PREFIX}/uploads`,
};
/** Machine-readable error codes the API is allowed to return. */
export const ErrorCodes = {
    BAD_REQUEST: 'BAD_REQUEST',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    FUND_NOT_FOUND: 'FUND_NOT_FOUND',
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    RATE_LIMITED: 'RATE_LIMITED',
    INTERNAL: 'INTERNAL',
    /** A generation run is already in flight. Returned as 409, never queued. */
    INSIGHTS_LOCKED: 'INSIGHTS_LOCKED',
    /** The month's spend ceiling would be exceeded. Refused before any API call. */
    INSIGHTS_BUDGET_EXCEEDED: 'INSIGHTS_BUDGET_EXCEEDED',
    /** No CLAUDE_API_KEY configured — the feature is off, not broken. */
    INSIGHTS_DISABLED: 'INSIGHTS_DISABLED',
};
//# sourceMappingURL=api.js.map